import {Component, OnInit} from '@angular/core';
import {PageComponent, PageSize} from '../../../shared/components/page/page.component';
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {LegalEntity} from '../../../domain/core/legal-entity.model';
import {CustomerService} from '../../../shared/services/customer.service';
import {ActivatedRoute} from '@angular/router';
import {VehicleBrand} from '../../../domain/core/vehicle-brand.model';
import {Country} from '../../../domain/core/country.model';
import {environment} from '../../../../environments/environment';
import {AppState} from '../../../shared/utils/AppState';
import {SelectComponent} from '../../../shared/components/select/select.component';
import {TextInputComponent} from '../../../shared/components/text-input/text-input.component';
import {NgClass} from '@angular/common';
import {EmailInputComponent} from '../../../shared/components/email-input/email-input.component';
import {RecaptchaLoaderService, ReCaptchaV3Service} from 'ng-recaptcha-2';
import {RegistrationPreferredDealerComponent} from '../components/registration-preferred-dealer/registration-preferred-dealer.component';
import {NewCustomerRequest} from '../../../domain/data/new-customer-request';
import {CardComponent} from '../../../shared/components/card/card.component';
import {ActivitySpinnerComponent} from '../../../shared/components/activity-spinner/activity-spinner.component';
import {RegistrationConsentComponent} from '../components/registration-consent/registration-consent.component';
import {Dealer} from '../../../domain/data/customer-client-response.data';
import {Title} from '@angular/platform-browser';

@Component({
  selector: 'app-registration-main',
  imports: [
    PageComponent,
    ReactiveFormsModule,
    SelectComponent,
    TextInputComponent,
    EmailInputComponent,
    RegistrationPreferredDealerComponent,
    NgClass,
    CardComponent,
    ActivitySpinnerComponent,
    RegistrationConsentComponent
  ],
  templateUrl: './registration-main.component.html',
  providers: [ReCaptchaV3Service, RecaptchaLoaderService]
})
export class RegistrationMainComponent implements OnInit {
  form!: FormGroup;
  registrationInProgress = false;
  registrationCompleted = false;
  legalEntity!: LegalEntity;
  allDealerList: Dealer[] = [];
  preferredDealer?: Dealer;
  consentVersion!: string;

  salutations = [
    { label: 'Ohne', value: null },
    { label: 'Frau', value: 'Frau' },
    { label: 'Herr', value: 'Herr' }
  ];

  customerTypes = [
    { value: 'private', label: 'Privatkunde' },
    { value: 'privatebusiness', label: 'Firma mit Ansprechpartner' },
    { value: 'company', label: 'Firma' }
  ];

  protected readonly PageSize = PageSize;

  constructor(
    private fb: FormBuilder,
    private customerService: CustomerService,
    private route: ActivatedRoute,
    private recaptchaV3Service: ReCaptchaV3Service,
    private titleService: Title
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      customerType: [this.customerTypes[0].value, Validators.required],
      companyName: [null],
      ust: [null],
      salutation: [this.salutations[0].value],
      title: [null],
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      street: [null],
      postcode: [null],
      city: [null],
      phoneHome: [null],
      phoneOffice: [null],
      phoneMobile: [null],
      email: [null, [Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/)]],
      dealerNumber: [null],
      communication: [null],
      profiling: [null],
      dataSharing: [null]
    }, { validators: atLeastOneConsentValidator });

    this.route.params.subscribe(params => {
      const brand = VehicleBrand.from(params['brand']);
      this.legalEntity = LegalEntity.from(brand, Country.from(environment.country));
      AppState.shared.setLegalEntity(this.legalEntity);
      this.titleService.setTitle(brand.name + ' Einwilligungscenter');
      const favIcon = document.getElementById('favIcon') as HTMLLinkElement | null;
      if (favIcon) {
        favIcon.href = `/assets/images/${brand.name.toLowerCase()}-favicon.png`;
        console.log('brand.name', brand.name);
      }
      this.fetchDealerInfo(brand);
      this.consentVersion = AppState.shared.consentVersion;
    });

    ['communication', 'profiling', 'dataSharing'].forEach(name => {
      this.form.get(name)?.valueChanges.subscribe(value => {
        if (value === false) {
          this.form.get(name)?.setValue(null, { emitEvent: false });
        }
      });
    });

    this.form.get('customerType')?.valueChanges.subscribe(value => {
      if (value === 'company') {
        this.form.get('firstName')?.clearValidators();
        this.form.get('lastName')?.clearValidators();
      } else {
        this.form.get('firstName')?.setValidators(Validators.required);
        this.form.get('lastName')?.setValidators(Validators.required);
      }
      this.form.get('firstName')?.updateValueAndValidity();
      this.form.get('lastName')?.updateValueAndValidity();
    });
  }

  fetchDealerInfo(brand: VehicleBrand) {
    this.recaptchaV3Service.execute('dealerInfo').subscribe(recaptchaToken => {
      this.customerService.getDealerInformation(brand, recaptchaToken).subscribe(dealerInfo => {
        this.allDealerList = dealerInfo.allDealerList;
      });
    });
  }

  onDealerSelected(dealer: Dealer | null) {
    this.preferredDealer = dealer ?? undefined;
    this.form.get('dealerNumber')?.setValue(dealer?.dealerNumber ?? '');
  }

  isControlValid(controlName: string) {
    const control = this.form.get(controlName);
    if (control.touched) {
      return control?.valid ?? false;
    }
    return true;
  }

  get emailErrorMessage() {
    const emailControl = this.form.get('email');

    if (emailControl.hasError('required')) {
      return 'Erforderlich';
    } else if (emailControl.hasError('pattern')) {
      return 'Ungültige E-Mail';
    } else {
      return '';
    }
  }

  get showCompanyName() {
    return this.form.get('customerType')?.value !== 'private';
  }

  get showUst() {
    return this.form.get('customerType')?.value === 'company';
  }

  get showNameInput() {
    return this.form.get('customerType')?.value !== 'company';
  }

  get showPhoneHome() {
    return this.form.get('customerType')?.value !== 'company';
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.registrationInProgress = true;
    const formValue = this.form.value;

    let newCustomerRequest: NewCustomerRequest;
    switch(formValue.customerType) {
      case 'private':
        newCustomerRequest = {
          b2c: true,
          b2b: false,
          company: false,
          email: formValue.email,
          dealerNumber: formValue.dealerNumber,
          firstName: formValue.firstName,
          lastName: formValue.lastName,
          salutation: formValue.salutation,
          title: formValue.title,
          street: formValue.street,
          postcode: formValue.postcode,
          city: formValue.city,
          phoneHome: formValue.phoneHome,
          phoneOffice: formValue.phoneOffice,
          phoneMobile: formValue.phoneMobile,
          communication: formValue.communication,
          profiling: formValue.profiling,
          dataSharing: formValue.dataSharing
        };
        break;
      case 'privatebusiness':
        newCustomerRequest = {
          b2c: false,
          b2b: true,
          company: false,
          email: formValue.email,
          dealerNumber: formValue.dealerNumber,
          firstName: formValue.firstName,
          lastName: formValue.lastName,
          companyName: formValue.companyName,
          salutation: formValue.salutation,
          title: formValue.title,
          street: formValue.street,
          postcode: formValue.postcode,
          city: formValue.city,
          phoneHome: formValue.phoneHome,
          phoneOffice: formValue.phoneOffice,
          phoneMobile: formValue.phoneMobile,
          communication: formValue.communication,
          profiling: formValue.profiling,
          dataSharing: formValue.dataSharing
        };
        break;
      case 'company':
        newCustomerRequest = {
          b2c: false,
          b2b: false,
          company: true,
          email: formValue.email,
          dealerNumber: formValue.dealerNumber,
          companyName: formValue.companyName,
          ust: formValue.ust,
          street: formValue.street,
          postcode: formValue.postcode,
          city: formValue.city,
          phoneHome: formValue.phoneHome,
          phoneOffice: formValue.phoneOffice,
          phoneMobile: formValue.phoneMobile,
          communication: formValue.communication,
          profiling: formValue.profiling,
          dataSharing: formValue.dataSharing
        };
        break;
    }

    this.recaptchaV3Service.execute('registration').subscribe(token => {
      this.customerService.registerCustomer(
        this.legalEntity.vehicleBrand,
        token,
        newCustomerRequest
      ).subscribe({
        next: (response) => {
          console.log('customer registered', response);
          this.registrationInProgress = false;
          this.registrationCompleted = true;
        },
        error: (error) => {
          console.error('registration failed', error);
        }
      });
    });
  }
}

export function atLeastOneConsentValidator(group: AbstractControl): ValidationErrors | null {
  const communication = group.get('communication')?.value;
  const profiling = group.get('profiling')?.value;
  const dataSharing = group.get('dataSharing')?.value;
  return communication || profiling || dataSharing ? null : { atLeastOneConsentRequired: true };
}
